import {
	forwardRef,
	memo,
	ReactElement,
	ReadonlyRefObject,
	Ref,
	RefCallback,
	RefObject,
	useCallback,
	useMemo,
	useRef,
	useState
} from "react";
import { TileGroupRenderState } from "./RenderState/common";
import { localize } from "client_legacy/ndaw/culture";
import {
	FontIconName,
	FormattedText,
	Icon,
	TextInput,
	useConfirmationDialog
} from "client/Components";
import classNames from "classnames";
import {
	ensureNotNil,
	isNotNil,
	setTrackingId
} from "client/Misc/utils";
import {
	TileGroupAccessKind,
	TileGroupType,
	TileGroupWidth,
	UserWorkspacePermissionsModel
} from "client/Api/Workspace/workspace";
import { TileGrid } from "./TileGrid";
import { TimeZone } from "client/DateAndTime/timezones";
import {
	DragCallbackArgs,
	useCallbackWithoutArguments,
	useDraggable,
	UseDraggableOptions,
	useExcludeFromDraggingRef,
	useSynchronizedRef
} from "client/Hooks";
import { TileContentDependencies } from "./useTileContentDependencies";
import { Coordinate } from "client/Misc/commonTypes";
import { DatabaseOrTempObjectId } from "client_legacy/ndaw/tempOrDatabaseId";
import { TileCallbacks } from "./Tile";
import { GridPosition } from "client/Api/Workspace/common";
import { useTileGroupEmbedUrlDialog } from "./TileGroupEmbedUrlDialog";
import { createObjectComparer } from "client/Misc/objectComparer";
import styles from "./TileGroup.module.less";
import {
	convertPositionToPx,
	GridCellPixelValues,
	GridPositionInPx
} from "./GridCellPixelValuesTracker";

export type TileGroupCallbacks = {
	onAddTiles: (tileGroupId: DatabaseOrTempObjectId) => void;
	onNameChange: (tileGroupId: DatabaseOrTempObjectId, newName: string) => void;
	onResize: (tileGroupId: DatabaseOrTempObjectId, width: TileGroupWidth) => void;
	onSharedTileGroupEdit: (tileGroupId: DatabaseOrTempObjectId) => void;
	onDelete: (tileGroupId: DatabaseOrTempObjectId) => void;
	onDragStart: (tileGroupId: DatabaseOrTempObjectId, args: DragCallbackArgs) => void;
	onDrag: (tileGroupId: DatabaseOrTempObjectId, args: DragCallbackArgs) => void;
	onDragStop: (tileGroupId: DatabaseOrTempObjectId, args: DragCallbackArgs) => void;
};

export type TileGroupProps = {
	isInEditMode: boolean;
	renderState: TileGroupRenderState;
	groupCellPixelValues: GridCellPixelValues;
	tileCellPixelValues: GridCellPixelValues;
	permissions: UserWorkspacePermissionsModel;
	userTimezone: TimeZone;
	tileContentDependencies: TileContentDependencies;
	tileGroupCallbacks: TileGroupCallbacks;
	tileCallbacks: TileCallbacks;
	canRearrangeTileGroups: boolean;
	isOnImage: boolean;
};

const TileGroupInternal = (props: TileGroupProps): ReactElement => {
	const {
		isInEditMode,
		renderState,
		groupCellPixelValues,
		tileCellPixelValues,
		permissions,
		userTimezone,
		tileContentDependencies,
		tileGroupCallbacks,
		tileCallbacks,
		canRearrangeTileGroups,
		isOnImage
	} = props;

	const [dragMovement, setDragMovement] = useState<Coordinate | null>(null);
	const [isNameBeingEdited, setIsNameBeingEdited] = useState<boolean>(false);
	const ref: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);
	const [positionWhenTileDragStarted, setPositionWhenTileDragStarted] = useState<GridPosition | null>();
	const positionRef: ReadonlyRefObject<GridPosition> = useSynchronizedRef(renderState.position);
	const [tileGroupEmbedUrlDialog, openTileGroupEmbedUrlDialog] = useTileGroupEmbedUrlDialog();
	const [confirmationDialog, openConfirmationDialog] = useConfirmationDialog();

	const isVisualEmpty = useMemo(
		(): boolean => {
			return !renderState.tileGrid
				.tiles
				.some((x) => x.isVisible);
		},
		[renderState.tileGrid.tiles]
	);

	const isBeingDragged: boolean = isNotNil(dragMovement);

	const { data: tileGroup } = renderState;
	const isEditable: boolean = tileGroup.accessKind === TileGroupAccessKind.Edit;

	const isMessageVisible: boolean = !isInEditMode && isVisualEmpty;

	const {
		onAddTiles,
		onDelete,
		onNameChange,
		onResize,
		onSharedTileGroupEdit,
		onDragStart: dragStartManagementHandler,
		onDrag: dragMoveManagementHandler,
		onDragStop: dragStopManagementHandler
	} = tileGroupCallbacks;

	const dragStartHandler = useCallback(
		(args: DragCallbackArgs): void => {
			setDragMovement(args.movement);

			dragStartManagementHandler(
				tileGroup.id,
				transformDragArgsToBeBasedOnThePositionOfTheTileGroup(
					args,
					ensureNotNil(ref.current)
				)
			);
		},
		[tileGroup.id, dragStartManagementHandler]
	);

	const dragMoveHandler = useCallback(
		(args: DragCallbackArgs): void => {
			setDragMovement(args.movement);

			dragMoveManagementHandler(
				tileGroup.id,
				transformDragArgsToBeBasedOnThePositionOfTheTileGroup(
					args,
					ensureNotNil(ref.current)
				)
			);
		},
		[dragMoveManagementHandler, tileGroup.id]
	);

	const dragStopHandler = useCallback(
		(args: DragCallbackArgs): void => {
			setDragMovement(null);

			dragStopManagementHandler(
				tileGroup.id,
				transformDragArgsToBeBasedOnThePositionOfTheTileGroup(
					args,
					ensureNotNil(ref.current)
				)
			);
		},
		[dragStopManagementHandler, tileGroup.id]
	);

	const dragOptions = useMemo(
		(): UseDraggableOptions => ({
			onStart: dragStartHandler,
			onMove: dragMoveHandler,
			onStop: dragStopHandler,
			isEnabled: isInEditMode
				&& canRearrangeTileGroups
		}),
		[
			canRearrangeTileGroups,
			dragMoveHandler,
			dragStartHandler,
			dragStopHandler,
			isInEditMode
		]
	);

	const {
		ref: dragHandleRef,
		excludeController
	} = useDraggable(dragOptions);

	const tileGroupNameExcludeRef: RefCallback<HTMLElement> = useExcludeFromDraggingRef(excludeController);

	const {
		onDragStart: tileDragStartManagementHandler,
		onDragStop: tileDragStopManagementHandler
	} = tileCallbacks;

	const tileDragStartHandler = useCallback(
		(tileId: number, args: DragCallbackArgs): void => {
			setPositionWhenTileDragStarted(positionRef.current);
			tileDragStartManagementHandler(tileId, args);
		},
		[positionRef, tileDragStartManagementHandler]
	);

	const tileDragStopHandler = useCallback(
		(tileId: number, args: DragCallbackArgs): void => {
			tileDragStopManagementHandler(tileId, args);
			setPositionWhenTileDragStarted(null);
		},
		[tileDragStopManagementHandler]
	);

	const editableNameClickHandlerHandler = (): void => {
		setIsNameBeingEdited(true);
	};

	const embedUrlClickHandler = (): void => {
		const tileGroupId: DatabaseOrTempObjectId = tileGroup.id;

		if (!tileGroupId.hasDatabaseId) {
			throw new Error("Cannot generate embed url for tilegroup because database id has not been assigned to it yet.");
		}

		openTileGroupEmbedUrlDialog({
			tileGroupId: tileGroupId.databaseId
		});
	};

	const deleteHandler = async (): Promise<void> => {
		const isEmpty: boolean = renderState.tileGrid
			.tiles
			.length === 0;

		if (!isEmpty) {
			const deleteConfirmation: TryResult<void> = await openConfirmationDialog(
				{
					title: (
						<FormattedText
							text={localize("_AreYouSureYouWantToDeleteName")}
							thingsToReplace={[
								{ what: "{Name}", filler: tileGroup.name }
							]}
						/>
					),
					confirmText: localize("_Delete"),
					children: localize("_ThisGroupWilBeDeletedPermanentlyAfterSavingTheWorkspace"),
					trackingId: "delete-tile-group-confirmation-dialog"
				}
			);

			if (!deleteConfirmation.wasSuccessful) {
				return;
			}
		}

		onDelete(tileGroup.id);
	};

	const newNameHandler = useCallback(
		(newName: string): void => {

			onNameChange(tileGroup.id, newName);
			setIsNameBeingEdited(false);
		},
		[onNameChange, tileGroup.id]
	);

	let positionInPx: GridPositionInPx = convertPositionToPx(renderState.position, groupCellPixelValues);

	if (dragMovement) {
		positionInPx = {
			left: positionInPx.left + dragMovement.x,
			top: positionInPx.top + dragMovement.y
		};
	}

	let displacementSinceTileDragStarted: GridPositionInPx | undefined;
	if (positionWhenTileDragStarted) {
		const displacement: GridPosition = {
			column: renderState.position.column - positionWhenTileDragStarted.column,
			row: renderState.position.row - positionWhenTileDragStarted.row
		};

		displacementSinceTileDragStarted = convertPositionToPx(displacement, groupCellPixelValues);
	}

	return (
		<>
			<div
				{...setTrackingId("tile-group", `|${tileGroup.id.toString()}`)}
				className={classNames(
					styles.root,
					{
						[styles.root_editMode]: isInEditMode,
						[styles.root_dragging]: isBeingDragged,
						[styles.root_onImage]: isOnImage
					}
				)}
				style={{
					...positionInPx,
					display: renderState.isVisible
						? undefined
						: "none"
				}}
				ref={ref}
			>
				<div
					{...setTrackingId("header")}
					className={styles.header}
					ref={dragHandleRef}
				>
					<div className={styles.headerContainer}>
						{isNameBeingEdited
							? (
								<TileGroupNameEditor
									initialValue={tileGroup.name}
									onNewValue={newNameHandler}
									ref={tileGroupNameExcludeRef}
								/>
							)
							: (
								<h3
									{...setTrackingId("name")}
									className={styles.name}
									onClick={isEditable && isInEditMode
										? editableNameClickHandlerHandler
										: undefined
									}
									style={{
										display: isNameBeingEdited
											? "none"
											: undefined
									}}
								>
									{tileGroup.name}
								</h3>
							)
						}

						{tileGroup.conditionalAccess && (
							<TileGroupHeaderButton
								kind="conditional-access"
								tooltip={tileGroup.conditionalAccess.reason}
							/>
						)}

						{!isInEditMode
							&& permissions.canEmbedTileGroups
							&& tileGroup.kind === TileGroupType.Shared
							&& (
								<TileGroupHeaderButton
									kind="embed-url"
									tooltip={localize("_GetEmbedUrl")}
									onClick={embedUrlClickHandler}
								/>
							)
						}

						{tileGroup.kind === TileGroupType.Shared && (
							<TileGroupHeaderButton
								kind="shared-tile-group"
								tooltip={localize("_SharedGroup")}
								onClick={
									isEditable
										? () => onSharedTileGroupEdit(tileGroup.id)
										: undefined
								}
							/>
						)}

						{isEditable && isInEditMode && (
							<>
								<TileGroupHeaderButton
									kind="add"
									tooltip={localize("_AddNewTiles")}
									onClick={() => onAddTiles(tileGroup.id)}
								/>

								{tileGroup.width === TileGroupWidth.Regular
									? (
										<TileGroupHeaderButton
											kind="enlarge"
											tooltip={localize("_Enlarge")}
											onClick={() => onResize(tileGroup.id, TileGroupWidth.Wide)}
										/>
									) : (
										<TileGroupHeaderButton
											kind="shrink"
											tooltip={localize("_Shrink")}
											onClick={() => onResize(tileGroup.id, TileGroupWidth.Regular)}
										/>
									)
								}

								<TileGroupHeaderButton
									kind="delete"
									tooltip={localize("_DeleteGroup")}
									onClick={deleteHandler}
								/>
							</>
						)}
						{canRearrangeTileGroups && isInEditMode && (
							<TileGroupHeaderButton kind="drag-handle" />
						)}
					</div>
				</div>
				<div className={styles.content}>
					{isMessageVisible && (
						<p
							{...setTrackingId("empty-message")}
							className={styles.emptyMessage}
						>
							{localize("_ThisGroupIsEmptyFillThisGroupWithTilesForAnOptimalExperience")}
						</p>
					)}

					<TileGrid
						renderState={renderState.tileGrid}
						isHidden={isMessageVisible}
						cellPixelValues={tileCellPixelValues}
						userTimezone={userTimezone}
						isTileGroupEditable={isEditable}
						isInEditMode={isInEditMode}
						tileContentDependencies={tileContentDependencies}
						tileCallbacks={{
							...tileCallbacks,
							onDragStart: tileDragStartHandler,
							onDragStop: tileDragStopHandler
						}}
						displacementSinceTileDragStarted={displacementSinceTileDragStarted}
					/>
				</div>
			</div>
			{tileGroupEmbedUrlDialog}
			{confirmationDialog}
		</>
	);
};
TileGroupInternal.displayName = "TileGroupInternal";

export const TileGroup = memo(
	TileGroupInternal,
	createObjectComparer<TileGroupProps>()
		.shallow([
			"tileCallbacks",
			"tileGroupCallbacks"
		])
		.equals([
			"canRearrangeTileGroups",
			"groupCellPixelValues",
			"isInEditMode",
			"permissions",
			"renderState",
			"tileCellPixelValues",
			"tileContentDependencies",
			"userTimezone",
			"isOnImage"
		])
		.getResult()
);
TileGroup.displayName = "TileGroup";

type TileGroupNameEditorProps = {
	initialValue: string;
	onNewValue: (newValue: string) => void;
};

const TileGroupNameEditor = forwardRef(
	(props: TileGroupNameEditorProps, ref: Ref<HTMLDivElement>): ReactElement => {
		const {
			initialValue,
			onNewValue
		} = props;

		const [value, setValue] = useState<string>(initialValue);

		const blurHandler = (): void => {
			onNewValue(value);
		};

		return (
			<div
				ref={ref}
				className={styles.nameInputContainer}
			>
				<TextInput
					trackingId="name"
					autoFocus="focus"
					value={value}
					onChange={setValue}
					onBlur={blurHandler}
				/>
			</div>
		);
	}
);
TileGroupNameEditor.displayName = "TileGroupNameEditor";

type TileGroupHeaderButtonProps = {
	kind: TileGroupHeaderButtonKind;
	tooltip?: string;
	onClick?: () => void;
};

const TileGroupHeaderButton = (props: TileGroupHeaderButtonProps): ReactElement => {
	const {
		kind,
		tooltip,
		onClick: onClickFromProps
	} = props;

	const onClick: (() => void) | undefined = useCallbackWithoutArguments(onClickFromProps);

	return (
		<div
			{...setTrackingId("button", `|${kind}`)}
			className={classNames(
				styles.button,
				buttonKindToClassMap[kind],
				{
					[styles.button_hoverable]: isNotNil(onClick)
				}
			)}
			title={tooltip}
			onClick={onClick}
		>
			<Icon size="large" value={buttonKindToIconMap[kind]} />
		</div>
	);
};
TileGroupHeaderButton.displayName = "TileGroupHeaderButton";

type TileGroupHeaderButtonKind =
	| "conditional-access"
	| "embed-url"
	| "shared-tile-group"
	| "add"
	| "enlarge"
	| "shrink"
	| "delete"
	| "drag-handle";

const buttonKindToIconMap: Record<TileGroupHeaderButtonKind, FontIconName> = {
	["conditional-access"]: "eye-slash",
	["embed-url"]: "link",
	["shared-tile-group"]: "user-group",
	["add"]: "plus",
	["enlarge"]: "arrow-up-right-and-arrow-down-left-from-center",
	["shrink"]: "arrow-down-left-and-arrow-up-right-to-center",
	["delete"]: "trash-can",
	["drag-handle"]: "grip-lines"
};

const buttonKindToClassMap: Record<TileGroupHeaderButtonKind, string | undefined> = {
	["conditional-access"]: styles.button_conditionalAccess,
	["embed-url"]: undefined,
	["shared-tile-group"]: undefined,
	["add"]: undefined,
	["enlarge"]: undefined,
	["shrink"]: undefined,
	["delete"]: undefined,
	["drag-handle"]: styles.button_dragHandle
};

// The drag handle (headeR) is not the topleft of the tilegroup, hence we convert the args to be based
// on the tilegroup instead of the drag handle
function transformDragArgsToBeBasedOnThePositionOfTheTileGroup(
	args: DragCallbackArgs,
	tileGroupElement: HTMLDivElement
): DragCallbackArgs {
	const { marginTop } = window.getComputedStyle(tileGroupElement);

	const clientX: number = args.clientX;
	const clientY: number = args.clientY - parseFloat(marginTop);

	return {
		...args,
		clientX,
		clientY
	};
}
